import { of, EMPTY, fromEvent, merge } from 'rxjs';
import { switchMap, catchError, tap, defaultIfEmpty } from 'rxjs/operators';
import { forkJoin } from 'rxjs';
import { fetchJson, postJson, deleteEntity } from './http.js';
import {
    renderTemplate,
    replaceViewContainer,
    showError,
    getParentAttr,
} from './ui.js';

/**
 * Renders the list of session decks into the given <ul> element. Each item
 * shows the student name, deck name and creation date, plus action buttons.
 * students is an array of { id, name } used to resolve student_id to a name.
 */
function renderSessions(listEl, decks, students) {
    listEl.innerHTML = '';
    for (let deck of decks) {
        let student = students.find((s) => s.id === deck.student_id);
        let studentName = student ? student.name : String(deck.student_id);
        let date = new Date(deck.created_ts).toLocaleDateString('de-DE');
        let li = renderTemplate('session-widget');
        li.setAttribute('data-session-id', `${deck.id}`);
        li.querySelector('.session-student-name').textContent = studentName;
        li.querySelector('.session-deck-name').textContent = deck.name;
        li.querySelector('.session-deck-date').textContent = date;
        listEl.appendChild(li);
    }
}

/**
 * Creates and returns the sessions list view Observable. Fetches all session
 * decks and all students on mount, renders them sorted by creation date
 * descending, and wires up per-row "Link generieren" and "Löschen" actions.
 */
export function createSessionsView() {
    return of(undefined).pipe(
        switchMap(() => {
            let el = renderTemplate('sessions-view');
            replaceViewContainer(el);

            let listEl = el.querySelector('.sessions-list');
            let linkArea = el.querySelector('.session-link-area');
            let linkEl = el.querySelector('.session-link');

            let students = [];

            function fetchAndRender() {
                return forkJoin([
                    fetchJson('/api/session-decks'),
                    fetchJson('/api/students'),
                ]).pipe(
                    tap(([decks, fetchedStudents]) => {
                        students = fetchedStudents;
                        renderSessions(listEl, decks, students);
                    }),
                    catchError((err) => {
                        showError(err);
                        return EMPTY;
                    })
                );
            }

            let load$ = fetchAndRender();

            let listActions$ = fromEvent(listEl, 'click').pipe(
                switchMap((e) => {
                    let generateBtn = e.target.closest('.generate-link-btn');
                    if (generateBtn) {
                        let deckId = getParentAttr(
                            generateBtn,
                            'data-session-id'
                        );
                        return postJson(
                            `/api/session-decks/${deckId}/start-token`,
                            {}
                        ).pipe(
                            tap((body) => {
                                let link = `${window.location.protocol}//${window.location.host}/session.html?t=${body.token}`;
                                linkEl.textContent = link;
                                linkEl.href = link;
                                linkArea.hidden = false;
                            }),
                            switchMap(() => EMPTY),
                            catchError((err) => {
                                showError(err);
                                return EMPTY;
                            })
                        );
                    }
                    let deleteBtn = e.target.closest('.delete-deck-btn');
                    if (deleteBtn) {
                        if (!window.confirm('Deck wirklich löschen?')) {
                            return EMPTY;
                        }
                        let deckId = getParentAttr(
                            deleteBtn,
                            'data-session-id'
                        );
                        return deleteEntity(
                            `/api/session-decks/${deckId}`
                        ).pipe(
                            defaultIfEmpty(null),
                            switchMap(() => fetchAndRender()),
                            catchError((err) => {
                                showError(err);
                                return EMPTY;
                            })
                        );
                    }
                    return EMPTY;
                })
            );

            return merge(load$, listActions$);
        })
    );
}
